import { useEffect } from 'react';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { type CADPosition } from '../../../utils/cadCoordinates';

/**
 * Fit camera to encompass the entire scene
 */
export function fitCamera(object: THREE.Object3D, camera: THREE.Camera) {
  const box = new THREE.Box3().setFromObject(object);
  if (box.isEmpty()) return;

  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());
  const maxDim = Math.max(size.x, size.y, size.z);

  if (maxDim === 0) return;

  const perspCam = camera as THREE.PerspectiveCamera;
  const fov = perspCam.fov * (Math.PI / 180);
  const distance = (maxDim / (2 * Math.tan(fov / 2))) * 1.8;

  perspCam.position.set(
    center.x + distance * 0.6,
    center.y + distance * 0.5,
    center.z + distance * 0.6
  );
  perspCam.lookAt(center);
  perspCam.near = distance * 0.01;
  perspCam.far = distance * 10;
  perspCam.updateProjectionMatrix();
}

export function FocusCameraOnPoint({
  point,
  modelRadius,
}: {
  point: CADPosition | null;
  modelRadius: number | null;
}) {
  const { camera } = useThree();
  useEffect(() => {
    if (!point) return;
    const distance = Math.max((modelRadius || 100) * 0.35, 8);
    camera.position.set(
      point[0] + distance,
      point[1] + distance * 0.7,
      point[2] + distance,
    );
    camera.lookAt(point[0], point[1], point[2]);
    if (camera instanceof THREE.PerspectiveCamera) {
      camera.near = Math.max(distance * 0.005, 0.01);
      camera.far = Math.max(distance * 30, 1000);
      camera.updateProjectionMatrix();
    }
  }, [camera, modelRadius, point]);
  return null;
}
